import { useContext, useEffect, useState } from 'react';

import { Bars3Icon } from '@heroicons/react/24/outline';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';

import { routes } from '../constants/routes';
import CreditContext from '../context/credits/context';
import { useCreditPopup } from '../hooks/useCreditPopup';
import { getIsResponsePopulated } from '../utils/responsePopulatedUtil';

import { Button } from './Button';
import CreditDisplay from './molecules/CreditDisplay';
import Recents from './molecules/Recents';
import ModalAlerts from './organisms/Modal/ModalAlerts';
import PopUpModal from './PopUpModal';
import PortalDropdown from './PortalDropdown';
import { UserMenu } from './UserMenu';

interface CreditCountNavProps {
  toggleSidebar: () => void;
  sidebarCollapsed: boolean;
  toggleMobileSidebar?: () => void;
}

const CreditCountNav = ({
  toggleSidebar,
  sidebarCollapsed,
  toggleMobileSidebar,
}: CreditCountNavProps) => {
  const { context: creditContext } = useContext(CreditContext);
  const { userCredit, isLoading } = creditContext;
  const [isClient, setIsClient] = useState(false);
  const { data: session } = useSession();
  const user = session?.user;
  const isLoggedIn = !!user;
  const router = useRouter();

  const [modalRoute, setModalRoute] = useState<string | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { creditPopUpData, setCreditPopUpData } = useCreditPopup(userCredit);

  useEffect(() => {
    setIsClient(true);
  }, []);

  function openModal(route: string) {
    setModalRoute(route);
    setShowConfirmationModal(true);
  }

  const handleRedirect = (route: string) => {
    if (getIsResponsePopulated()) {
      openModal(route);
    } else {
      router.push(route);
    }
  };

  const handleConfirmClose = () => {
    if (modalRoute) {
      router.push(modalRoute);
    }
    setShowConfirmationModal(false);
  };
  const shouldShowMobileSidebar = window.innerWidth < 768;

  const handleSidebarToggle = () => {
    // Toggle mobile sidebar on small screens
    if (shouldShowMobileSidebar && toggleMobileSidebar) {
      toggleMobileSidebar();
    } else {
      // Toggle regular sidebar on larger screens
      toggleSidebar();
    }
  };

  return (
    <div className="sticky top-0 z-30 flex h-[65px] items-center justify-between border-b border-black/10 bg-white px-3 py-1">
      <PopUpModal {...creditPopUpData} setIsOpen={setCreditPopUpData} />
      <div className="flex h-full shrink-0 items-center gap-4 px-2">
        <Button
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="relative flex h-10 w-10 items-center justify-center rounded-md text-gray-600 hover:text-blue-600"
          onClick={handleSidebarToggle}
        >
          <Bars3Icon className="h-full w-full" />
        </Button>
        <div className="block h-10 border-l border-black/10" />
        <Button
          className="flex h-full cursor-pointer"
          onClick={() => {
            handleRedirect(routes.LANDING);
          }}
        >
          <img
            alt="logo"
            height={48}
            src={`${process.env.NEXT_PUBLIC_GOOGLE_CDN}/images/logo.svg`}
            width={48}
          />
          <div className="ml-3 flex h-full flex-col justify-center text-left text-sm font-bold leading-none">
            <span>
              Lesson
              <br />
              Planner PH
            </span>
          </div>
        </Button>
        {isClient && isLoggedIn && <Recents className="ml-3" />}
      </div>
      <div className="flex h-full items-center justify-end gap-4">
        <PortalDropdown />
        {isLoggedIn && (
          <CreditDisplay
            className="hidden md:flex"
            isLoading={isLoading}
            userCredit={userCredit}
            onRedirect={handleRedirect}
          />
        )}
        {isClient && (
          <button className="hidden md:flex" type="button">
            <UserMenu creditsData={userCredit} />
          </button>
        )}
      </div>
      <ModalAlerts
        handleConfirmClose={handleConfirmClose}
        setShowConfirmationModal={setShowConfirmationModal}
        showConfirmationModal={showConfirmationModal}
      />
    </div>
  );
};

export default CreditCountNav;
